import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable destructive-action confirmation.
 * Requires the admin password + typing CONFIRM, shows a warning and an
 * "affected records" summary. `onConfirm(password)` performs the action.
 */
export function DangerConfirm({ open, onOpenChange, title, description, affectedText, onConfirm, busy, children, confirmLabel = 'Delete' }) {
  const [pw, setPw] = useState('');
  const [cf, setCf] = useState('');

  useEffect(() => { if (!open) { setPw(''); setCf(''); } }, [open]);

  const submit = () => {
    if (!pw) { toast.error('Enter your admin password'); return; }
    if (cf.trim().toUpperCase() !== 'CONFIRM') { toast.error('Type CONFIRM to proceed'); return; }
    onConfirm(pw);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="danger-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-[hsl(var(--destructive))]">
            <AlertTriangle className="h-5 w-5" /> {title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
          {affectedText && (
            <div className="rounded-lg border bg-[hsl(var(--secondary))] p-3 text-sm" data-testid="danger-affected-text">
              {affectedText}
            </div>
          )}
          {children}
          <div><Label>Confirm your admin password</Label>
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="danger-password-input" className="mt-1.5" />
          </div>
          <div><Label>Type <b>CONFIRM</b> to proceed</Label>
            <Input value={cf} onChange={(e) => setCf(e.target.value)} placeholder="CONFIRM" data-testid="danger-confirm-input" className="mt-1.5" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={busy} data-testid="danger-confirm-button">{busy ? 'Working…' : confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
